import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { programService, dashboardService, partnerService } from '../services/api';
import ProgramPricingManagement from './ProgramPricingManagement';
import PathwaySelectionBuilder from './PathwaySelectionBuilder';
import { CiueProfile, Program, DashboardStats, Partner, ProgramPricingResponse, ProgramBreakEvenRow } from '../types';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend, BarChart, Bar, XAxis, YAxis } from 'recharts';

const PIE_COLORS = ['#2563eb', '#10b981', '#f59e0b', '#ec4899', '#8b5cf6', '#0ea5e9', '#14b8a6', '#f97316', '#a855f7'];

const ProgramsManagement: React.FC = () => {
  const { user } = useAuth();
  const [programs, setPrograms] = useState<Program[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [metricsError, setMetricsError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'view' | 'edit' | null>(null);
  const [selectedProgram, setSelectedProgram] = useState<Program | null>(null);
  const [buildStep, setBuildStep] = useState(1);
  const [showMetricsModal, setShowMetricsModal] = useState(false);
  const [metricsProgram, setMetricsProgram] = useState<Program | null>(null);
  const [showBuilderModal, setShowBuilderModal] = useState(false);
  const [builderProgramId, setBuilderProgramId] = useState<number | null>(null);
  const [builderProgram, setBuilderProgram] = useState<Program | null>(null);
  const [pricingData, setPricingData] = useState<ProgramPricingResponse | null>(null);
  const [pricingDraft, setPricingDraft] = useState<ProgramPricingResponse | null>(null);
  const [pricingLoading, setPricingLoading] = useState(false);
  const [cohortSaving, setCohortSaving] = useState(false);
  const [selectedPathwayId, setSelectedPathwayId] = useState<number | null>(null);
  const [viewPricingLoading, setViewPricingLoading] = useState(false);
  const [hostUniversities, setHostUniversities] = useState<Partner[]>([]);
  const [selectedHostUniversityIds, setSelectedHostUniversityIds] = useState<number[]>([]);
  const [initialHostUniversityIds, setInitialHostUniversityIds] = useState<number[]>([]);
  const [dashboardStats, setDashboardStats] = useState<DashboardStats | null>(null);
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [programCohortMeta, setProgramCohortMeta] = useState<Record<number, { durationDays: number | null; seatsAvailable: number | null }>>({});
  type CohortDraft = {
    id?: number;
    program_id?: number;
    name: string;
    starts_on?: string | null;
    ends_on?: string | null;
    application_deadline?: string | null;
    capacity?: number | null;
    seats_available?: number | null;
    selling_price?: number | null;
    estimated_selling_price?: number | null;
    classes_offered?: string[] | null;
    status?: string | null;
    is_active?: boolean;
  };

  type CohortPayload = {
    name: string;
    starts_on?: string;
    ends_on?: string;
    application_deadline?: string;
    capacity?: number;
    seats_available?: number;
    selling_price?: number;
    estimated_selling_price?: number;
    classes_offered?: string[];
    status?: string;
    is_active?: boolean;
  };

  const [cohorts, setCohorts] = useState<CohortDraft[]>([]);

  const defaultCiueProfile: CiueProfile = {
    housing_location: '',
    room_arrangement: '',
    nearby_facilities: '',
    housing_support: '',
    prayer_access: '',
    meal_plan_details: '',
    halal_food_access: '',
    grocery_access: '',
    muslim_community_presence: '',
    student_orgs: '',
    social_support: '',
    program_expectations: '',
    prior_islamic_studies: '',
    arabic_requirement_details: '',
    expected_conduct: '',
    travel_readiness: '',
    application_steps: '',
    selection_process: '',
    daily_schedule_overview: '',
    inclusion_statement: '',
    academic_support: '',
    cultural_support: '',
    spiritual_support: '',
    logistical_support: '',
    representation_commitment: '',
  };

  const [ciueProfile, setCiueProfile] = useState<CiueProfile>(defaultCiueProfile);
  const [formData, setFormData] = useState({
    slug: '',
    title: '',
    short_tagline: '',
    description: '',
    overview: '',
    mission_statement: '',
    location: '',
    primary_city: '',
    primary_country: '',
    duration: '',
    academic_focus: '',
    highlights: [''],
    image_url: '',
    hero_image_url: '',
    gallery_image_urls: '',
    language_of_instruction: '',
    language_level_required: '',
    academic_format: '',
    credit_or_certificate_info: '',
    housing_type: '',
    housing_description: '',
    meals_included: false,
    food_description: '',
    prayer_environment: '',
    community_life_description: '',
    safety_overview: '',
    weekly_structure_description: '',
    cultural_immersion_description: '',
    excursions_description: '',
    included_features: '',
    optional_trips_description: '',
    optional_courses_description: '',
    ideal_participant_description: '',
    not_ideal_for_description: '',
    tags: '',
    is_featured: false,
    is_active: true,
    bundle_discount_amount: '',
    bundle_discount_description: '',
    portal_summary: '',
    portal_benefits: '',
    portal_core_tracks_overview: '',
    portal_optional_trips_overview: '',
    portal_academics: '',
    portal_faq: ''
  });
  const sectionTitleClassName = 'text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-300';
  const labelClassName = 'block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1';
  const smallLabelClassName = 'block text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-300 mb-1';
  const mutedTextClassName = 'text-sm text-slate-500 dark:text-slate-300';
  const inputClassName = 'w-full px-3 py-2 border border-slate-300/80 dark:border-slate-700/80 rounded-lg bg-white/90 dark:bg-slate-900/60 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/40';
  const selectClassName = 'w-full px-3 py-2 border border-slate-300/80 dark:border-slate-700/80 rounded-lg bg-white/90 dark:bg-slate-900/60 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-500/40';
  const textareaClassName = 'w-full px-3 py-2 border border-slate-300/80 dark:border-slate-700/80 rounded-lg bg-white/90 dark:bg-slate-900/60 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/40';

  const formatDate = (value?: string | null) => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleDateString();
  };

  const formatCurrencyValue = (value?: number | null, currency?: string | null) => {
    if (value == null) return '—';
    const resolvedCurrency = currency || 'USD';
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: resolvedCurrency,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const formatDurationDays = (days?: number | null) => {
    if (!days) return 'TBD';
    return `${days} day${days === 1 ? '' : 's'}`;
  };

  const computeDurationDays = useCallback((start?: string | null, end?: string | null) => {
    if (!start || !end) return null;
    const startDate = new Date(start);
    const endDate = new Date(end);
    if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) return null;
    const diffMs = endDate.getTime() - startDate.getTime();
    if (diffMs < 0) return null;
    return Math.ceil(diffMs / (1000 * 60 * 60 * 24)) + 1;
  }, []);

  const fetchPrograms = useCallback(async () => {
    try {
      setLoading(true);
      const list = await programService.getAllPrograms();
      setPrograms(list);
      const cohortMetaEntries = await Promise.all(list.map(async (program) => {
        try {
          const cohorts = await programService.getProgramCohorts(program.id);
          const startDates = cohorts.map((cohort: any) => cohort.starts_on).filter(Boolean);
          const endDates = cohorts.map((cohort: any) => cohort.ends_on).filter(Boolean);
          const earliestStart = startDates.length ? startDates.reduce((min: string, value: string) => (
            new Date(value) < new Date(min) ? value : min
          ), startDates[0]) : null;
          const latestEnd = endDates.length ? endDates.reduce((max: string, value: string) => (
            new Date(value) > new Date(max) ? value : max
          ), endDates[0]) : null;
          const durationDays = computeDurationDays(earliestStart, latestEnd);
          const totalCapacity = cohorts.reduce((sum: number, cohort: any) => sum + (Number(cohort.capacity) || 0), 0);
          const seatsAvailable = totalCapacity ? Math.max(0, totalCapacity - (program.enrolled_student_count ?? 0)) : null;
          return [program.id, { durationDays, seatsAvailable }] as const;
        } catch (err) {
          console.error('Failed to load cohorts for program', program.id, err);
          return [program.id, { durationDays: null, seatsAvailable: null }] as const;
        }
      }));
      setProgramCohortMeta(Object.fromEntries(cohortMetaEntries));
    } catch (err) {
      console.error('Error fetching programs:', err);
      setError('Failed to load programs');
    } finally {
      setLoading(false);
    }
  }, [computeDurationDays]);

  useEffect(() => {
    if (user?.is_admin) {
      fetchPrograms();
      fetchDashboardStats();
      fetchHostUniversities();
    }
  }, [user, fetchPrograms]);

  const fetchDashboardStats = async () => {
    try {
      setMetricsLoading(true);
      const stats = await dashboardService.getStats();
      setDashboardStats(stats);
      setMetricsError(null);
    } catch (err) {
      console.error('Error fetching dashboard stats:', err);
      setMetricsError('Unable to load student metrics');
    } finally {
      setMetricsLoading(false);
    }
  };

  const fetchHostUniversities = async () => {
    try {
      const partners = await partnerService.getPartners('HOST_UNIVERSITY');
      setHostUniversities(partners);
    } catch (err) {
      console.error('Error fetching host universities:', err);
    }
  };

  const syncPricingDraftWithCohorts = useCallback((draft: ProgramPricingResponse | null, nextCohorts: typeof cohorts) => {
    if (!draft || !draft.cohorts || draft.cohorts.length === 0) return draft;
    const byId = new Map(nextCohorts.filter((cohort) => cohort.id != null).map((cohort) => [cohort.id as number, cohort]));
    const synced = draft.cohorts.map((item) => {
      const source = item.id != null ? byId.get(item.id) : null;
      if (!source) return item;
      return {
        ...item,
        name: source.name ?? item.name,
        start_date: source.starts_on ?? item.start_date,
        end_date: source.ends_on ?? item.end_date,
        capacity: source.capacity ?? item.capacity,
        selling_price: source.selling_price ?? item.selling_price,
      };
    });
    return { ...draft, cohorts: synced };
  }, []);

  const fetchCohorts = useCallback(async (programId: number) => {
    try {
      const response = await programService.getProgramCohorts(programId);
      setCohorts(response);
      setPricingDraft((prev) => syncPricingDraftWithCohorts(prev, response));
    } catch (err) {
      console.error('Error fetching cohorts:', err);
      setCohorts([]);
    }
  }, [syncPricingDraftWithCohorts]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => {
      const next = {
        ...prev,
        [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
      };
      return next;
    });
  };

  const updateCiueField = (field: keyof CiueProfile, value: string) => {
    setCiueProfile((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleHighlightChange = (index: number, value: string) => {
    const newHighlights = [...formData.highlights];
    newHighlights[index] = value;
    setFormData(prev => ({ ...prev, highlights: newHighlights }));
  };

  const addHighlight = () => {
    setFormData(prev => ({ ...prev, highlights: [...prev.highlights, ''] }));
  };

  const removeHighlight = (index: number) => {
    setFormData(prev => ({
      ...prev,
      highlights: prev.highlights.filter((_, i) => i !== index)
    }));
  };

  const resetForm = () => {
    setFormData({
      slug: '',
      title: '',
      short_tagline: '',
      description: '',
      overview: '',
      mission_statement: '',
      location: '',
      primary_city: '',
      primary_country: '',
      duration: '',
      academic_focus: '',
      highlights: [''],
      image_url: '',
      hero_image_url: '',
      gallery_image_urls: '',
      language_of_instruction: '',
      language_level_required: '',
      academic_format: '',
      credit_or_certificate_info: '',
      housing_type: '',
      housing_description: '',
      meals_included: false,
      food_description: '',
      prayer_environment: '',
      community_life_description: '',
      safety_overview: '',
      weekly_structure_description: '',
      cultural_immersion_description: '',
      excursions_description: '',
      included_features: '',
      optional_trips_description: '',
      optional_courses_description: '',
      ideal_participant_description: '',
      not_ideal_for_description: '',
      tags: '',
      is_featured: false,
      is_active: true,
      bundle_discount_amount: '',
      bundle_discount_description: '',
      portal_summary: '',
      portal_benefits: '',
      portal_core_tracks_overview: '',
      portal_optional_trips_overview: '',
      portal_academics: '',
      portal_faq: ''
    });
    setCiueProfile(defaultCiueProfile);
    setSelectedProgram(null);
    setCohorts([]);
    setPricingDraft(null);
  };

  const openCreateModal = () => {
    resetForm();
    setModalMode('create');
    setBuildStep(1);
    setSelectedHostUniversityIds([]);
    setInitialHostUniversityIds([]);
    setShowModal(true);
  };

  const startEditMode = () => {
    if (!selectedProgram) return;
    setModalMode('edit');
    setBuildStep(1);
  };

  const startPricingStep = () => {
    if (!selectedProgram) return;
    setModalMode('edit');
    setBuildStep(3);
  };

  const closeModal = () => {
    setShowModal(false);
    setModalMode(null);
    setBuildStep(1);
    setSelectedHostUniversityIds([]);
    setInitialHostUniversityIds([]);
    resetForm();
  };

  const openMetricsModal = async (program: Program) => {
    setMetricsProgram(program);
    setShowMetricsModal(true);
    setPricingLoading(true);
    try {
      const response = await programService.getProgramPricing(program.id);
      setPricingData(response);
      const firstTrack = (response.cohorts ?? [])[0];
      setSelectedPathwayId(firstTrack?.id ?? null);
    } catch (err) {
      setPricingData(null);
    } finally {
      setPricingLoading(false);
    }
  };

  const closeMetricsModal = () => {
    setShowMetricsModal(false);
    setMetricsProgram(null);
    setPricingData(null);
    setSelectedPathwayId(null);
  };

  const openBuilderModal = (program: Program) => {
    setBuilderProgramId(program.id);
    setBuilderProgram(program);
    setShowBuilderModal(true);
  };

  const closeBuilderModal = () => {
    setShowBuilderModal(false);
    setBuilderProgramId(null);
    setBuilderProgram(null);
  };


  const openProgramModal = (program: Program) => {
    setCohorts([]);
    setPricingDraft(null);
    setSelectedProgram(program);
    setBuildStep(1);
    const hostIds = (program.partners || [])
      .filter((partner) => partner.partner_type === 'HOST_UNIVERSITY')
      .map((partner) => partner.id);
    setSelectedHostUniversityIds(hostIds);
    setInitialHostUniversityIds(hostIds);
    setFormData({
      slug: program.slug || '',
      title: program.title,
      short_tagline: program.short_tagline || '',
      description: program.description || '',
      overview: program.overview || '',
      mission_statement: program.mission_statement || '',
      location: program.location || '',
      primary_city: program.primary_city || '',
      primary_country: program.primary_country || '',
      duration: program.duration || '',
      academic_focus: program.academic_focus || '',
      highlights: program.highlights.length > 0 ? program.highlights : [''],
      image_url: program.image_url || '',
      hero_image_url: program.hero_image_url || '',
      gallery_image_urls: (program.gallery_image_urls || []).join('\n'),
      language_of_instruction: program.language_of_instruction || '',
      language_level_required: program.language_level_required || '',
      academic_format: program.academic_format || '',
      credit_or_certificate_info: program.credit_or_certificate_info || '',
      housing_type: program.housing_type || '',
      housing_description: program.housing_description || '',
      meals_included: Boolean(program.meals_included),
      food_description: program.food_description || '',
      prayer_environment: program.prayer_environment || '',
      community_life_description: program.community_life_description || '',
      safety_overview: program.safety_overview || '',
      weekly_structure_description: program.weekly_structure_description || '',
      cultural_immersion_description: program.cultural_immersion_description || '',
      excursions_description: program.excursions_description || '',
      included_features: (program.included_features || []).join('\n'),
      optional_trips_description: program.optional_trips_description || '',
      optional_courses_description: program.optional_courses_description || '',
      ideal_participant_description: program.ideal_participant_description || '',
      not_ideal_for_description: program.not_ideal_for_description || '',
      tags: program.tags?.join(', ') || '',
      is_featured: Boolean(program.is_featured),
      is_active: program.is_active,
      bundle_discount_amount: program.bundle_discount_amount != null ? String(program.bundle_discount_amount) : '',
      bundle_discount_description: program.bundle_discount_description || '',
      portal_summary: program.portal_summary || '',
      portal_benefits: program.portal_benefits || '',
      portal_core_tracks_overview: program.portal_core_tracks_overview || '',
      portal_optional_trips_overview: program.portal_optional_trips_overview || '',
      portal_academics: program.portal_academics || '',
      portal_faq: program.portal_faq || ''
    });
    setCiueProfile({
      ...defaultCiueProfile,
      ...(program.ciue_profile || {}),
    });
    setModalMode('view');
    setShowModal(true);
  };

  const saveProgram = async () => {
    const parseListField = (value: string) => (
      value
        .split(/[\n,]/g)
        .map((item) => item.trim())
        .filter(Boolean)
    );
    const tags = formData.tags
      .split(',')
      .map(tag => tag.trim())
      .filter(Boolean);
    const computedDuration = formData.duration;
    const programData: Partial<Program> = {
      slug: formData.slug.trim() || null,
      title: formData.title,
      short_tagline: formData.short_tagline || null,
      description: formData.description,
      overview: formData.overview || null,
      mission_statement: formData.mission_statement || null,
      location: formData.location,
      primary_city: formData.primary_city || null,
      primary_country: formData.primary_country || null,
      duration: computedDuration || '',
      academic_focus: formData.academic_focus || null,
      highlights: formData.highlights.filter(h => h.trim() !== ''),
      image_url: formData.image_url,
      hero_image_url: formData.hero_image_url || null,
      gallery_image_urls: parseListField(formData.gallery_image_urls),
      language_of_instruction: formData.language_of_instruction || null,
      language_level_required: formData.language_level_required || null,
      academic_format: formData.academic_format || null,
      credit_or_certificate_info: formData.credit_or_certificate_info || null,
      housing_type: formData.housing_type || null,
      housing_description: formData.housing_description || null,
      meals_included: formData.meals_included,
      food_description: formData.food_description || null,
      prayer_environment: formData.prayer_environment || null,
      community_life_description: formData.community_life_description || null,
      safety_overview: formData.safety_overview || null,
      weekly_structure_description: formData.weekly_structure_description || null,
      cultural_immersion_description: formData.cultural_immersion_description || null,
      excursions_description: formData.excursions_description || null,
      included_features: parseListField(formData.included_features),
      optional_trips_description: formData.optional_trips_description || null,
      optional_courses_description: formData.optional_courses_description || null,
      ideal_participant_description: formData.ideal_participant_description || null,
      not_ideal_for_description: formData.not_ideal_for_description || null,
      tags,
      addon_trips: [],
      is_featured: formData.is_featured,
      is_active: formData.is_active,
      bundle_discount_amount: (() => {
        const raw = formData.bundle_discount_amount.trim();
        if (raw === '') return null;
        const parsed = Number(raw);
        return Number.isNaN(parsed) ? null : parsed;
      })(),
      bundle_discount_description: formData.bundle_discount_description.trim() || null,
      portal_summary: formData.portal_summary || null,
      portal_benefits: formData.portal_benefits || null,
      portal_core_tracks_overview: formData.portal_core_tracks_overview || null,
      portal_optional_trips_overview: formData.portal_optional_trips_overview || null,
      portal_academics: formData.portal_academics || null,
      portal_faq: formData.portal_faq || null,
      ciue_profile: (() => {
        const cleaned = Object.entries(ciueProfile).reduce<Record<string, string>>((acc, [key, value]) => {
          const trimmed = (value || '').trim();
          if (trimmed) acc[key] = trimmed;
          return acc;
        }, {});
        return Object.keys(cleaned).length > 0 ? cleaned : null;
      })(),
    };

    let savedProgram: Program;
    if (modalMode === 'edit' && selectedProgram) {
      savedProgram = await programService.updateProgram(selectedProgram.id, programData);
    } else {
      savedProgram = await programService.createProgram(programData);
    }

    if (hostUniversities.length > 0) {
      const selectedSet = new Set(selectedHostUniversityIds);
      const initialSet = new Set(initialHostUniversityIds);
      await Promise.all(
        hostUniversities.map(async (partner) => {
          const hasProgram = partner.program_ids?.includes(savedProgram.id);
          const shouldHave = selectedSet.has(partner.id);
          const didHave = initialSet.has(partner.id);
          if (!shouldHave && !didHave && !hasProgram) return;
          if (shouldHave && hasProgram) return;
          if (!shouldHave && !hasProgram) return;
          const nextProgramIds = shouldHave
            ? Array.from(new Set([...(partner.program_ids || []), savedProgram.id]))
            : (partner.program_ids || []).filter((id) => id !== savedProgram.id);
          await partnerService.updatePartner(partner.id, { program_ids: nextProgramIds });
        })
      );
    }

    if (pricingDraft && pricingDraft.program_id === savedProgram.id) {
      const syncedDraft = syncPricingDraftWithCohorts(pricingDraft, cohorts);
      await programService.updateProgramPricing(savedProgram.id, syncedDraft || pricingDraft);
    }

    await fetchPrograms();
    return savedProgram;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const savedProgram = await saveProgram();
      if (savedProgram?.id && cohorts.length > 0) {
        await saveCohorts(savedProgram.id);
      }
      closeModal();
    } catch (err) {
      console.error('Error saving program:', err);
      setError('Failed to save program');
    }
  };

  const handleStepAdvance = async () => {
    try {
      let savedProgram = selectedProgram;
      if (!savedProgram || modalMode === 'create') {
        savedProgram = await saveProgram();
        setSelectedProgram(savedProgram);
        setModalMode('edit');
      } else {
        savedProgram = await saveProgram();
      }
      if (buildStep === 3 && savedProgram) {
        await saveCohorts(savedProgram.id);
        if (pricingDraft && pricingDraft.program_id === savedProgram.id) {
          const syncedDraft = syncPricingDraftWithCohorts(pricingDraft, cohorts);
          await programService.updateProgramPricing(savedProgram.id, syncedDraft || pricingDraft);
        }
      }
      setBuildStep((prev) => Math.min(prev + 1, totalSteps));
    } catch (err) {
      console.error('Error saving step:', err);
      setError('Failed to save program');
    }
  };

  const handleDelete = async (program: Program) => {
    if (!window.confirm(`Delete program "${program.title}"?`)) {
      return;
    }
    try {
      await programService.deleteProgram(program.id);
      await fetchPrograms();
      if (selectedProgram?.id === program.id) {
        closeModal();
      }
    } catch (err) {
      console.error('Error deleting program:', err);
      setError('Failed to delete program');
    }
  };

  const addCohort = () => {
    setCohorts((prev) => ([
      ...prev,
      {
        name: '',
        starts_on: null,
        ends_on: null,
        application_deadline: null,
        capacity: null,
        seats_available: null,
        selling_price: null,
        estimated_selling_price: null,
        classes_offered: [],
        status: 'OPEN',
        is_active: true,
      }
    ]));
  };

  const updateCohortField = (index: number, field: string, value: any) => {
    setCohorts((prev) => {
      const next = prev.map((cohort, idx) => (
        idx === index ? { ...cohort, [field]: value } : cohort
      ));
      setPricingDraft((draft) => syncPricingDraftWithCohorts(draft, next));
      return next;
    });
  };

  const buildCohortPayload = (cohort: CohortDraft): CohortPayload => {
    const payload: CohortPayload = {
      name: (cohort.name ?? '').trim() || 'Untitled Cohort',
      status: cohort.status || 'OPEN',
      is_active: cohort.is_active ?? true,
    };

    if (cohort.starts_on) payload.starts_on = cohort.starts_on;
    if (cohort.ends_on) payload.ends_on = cohort.ends_on;
    if (cohort.application_deadline) payload.application_deadline = cohort.application_deadline;
    if (typeof cohort.capacity === 'number') payload.capacity = cohort.capacity;
    if (typeof cohort.seats_available === 'number') payload.seats_available = cohort.seats_available;
    if (typeof cohort.selling_price === 'number') payload.selling_price = cohort.selling_price;
    if (typeof cohort.estimated_selling_price === 'number') {
      payload.estimated_selling_price = cohort.estimated_selling_price;
    }
    if (Array.isArray(cohort.classes_offered)) {
      payload.classes_offered = cohort.classes_offered
        .map((item) => item?.trim())
        .filter(Boolean) as string[];
    }

    return payload;
  };

  const removeCohort = async (index: number) => {
    const cohort = cohorts[index];
    if (cohort?.id && selectedProgram?.id) {
      try {
        await programService.deleteProgramCohort(selectedProgram.id, cohort.id);
      } catch (err) {
        console.error('Failed to delete cohort', err);
        return;
      }
    }
    setCohorts((prev) => prev.filter((_, idx) => idx !== index));
  };

  const saveCohorts = async (programId: number) => {
    const tasks = cohorts.map((cohort) => (async () => {
      const payload = buildCohortPayload(cohort);
      const matchesProgram = cohort.program_id == null || cohort.program_id === programId;
      if (cohort.id && matchesProgram) {
        try {
          return await programService.updateProgramCohort(programId, cohort.id, payload);
        } catch (err: any) {
          const status = err?.response?.status;
          if (status === 404) {
            return await programService.createProgramCohort(programId, payload);
          }
          throw err;
        }
      }
      return await programService.createProgramCohort(programId, payload);
    })());
    const results = await Promise.allSettled(tasks);
    const failures = results.filter((result) => result.status === 'rejected');
    if (failures.length) {
      console.error('Some cohorts failed to save', failures);
      setError('Some cohorts failed to save.');
    }
    await fetchCohorts(programId);
  };

  const handleCohortSave = async () => {
    if (!selectedProgram?.id) return;
    try {
      setCohortSaving(true);
      await saveCohorts(selectedProgram.id);
      if (pricingDraft && pricingDraft.program_id === selectedProgram.id) {
        const syncedDraft = syncPricingDraftWithCohorts(pricingDraft, cohorts);
        await programService.updateProgramPricing(selectedProgram.id, syncedDraft || pricingDraft);
      }
    } catch (err) {
      console.error('Error saving cohorts:', err);
      setError('Failed to save cohorts');
    } finally {
      setCohortSaving(false);
    }
  };

  const handlePrintMetrics = () => {
    window.print();
  };

  const getHostUniversities = (program: Program): string[] => {
    if (program.host_universities && program.host_universities.length > 0) {
      return program.host_universities;
    }
    if (program.partners && program.partners.length > 0) {
      return program.partners
        .filter((partner) => partner.partner_type === 'HOST_UNIVERSITY')
        .map((partner) => partner.name);
    }
    return [];
  };

  const totalPrograms = programs.length;
  const activePrograms = programs.filter(program => program.is_active).length;
  const enrolledCount = dashboardStats?.stage_counts_by_code?.active_student || 0;
  const stepLabels = ['Basics', 'Details', 'Cohorts & Extensions', 'Program Portal', 'Analysis (Break-Even)'];
  const totalSteps = stepLabels.length;
  const bundleCurrency = pricingDraft?.base_currency
    || pricingData?.base_currency
    || selectedProgram?.base_currency
    || selectedProgram?.currency
    || 'USD';
  const bundleDiscountAmountValue = (() => {
    const raw = formData.bundle_discount_amount.trim();
    if (!raw) return null;
    const parsed = Number(raw);
    return Number.isNaN(parsed) ? null : parsed;
  })();

  const metricsTracks = useMemo(() => (
    pricingData?.cohorts ?? []
  ), [pricingData]);

  const metricsPathway = useMemo(() => (
    metricsTracks.find((pathway) => pathway.id === selectedPathwayId) ?? null
  ), [metricsTracks, selectedPathwayId]);

  const metricsBreakEven = useMemo<ProgramBreakEvenRow | null>(() => (
    pricingData?.break_even.find((row) => row.pathway_id === selectedPathwayId) ?? null
  ), [pricingData, selectedPathwayId]);

  const componentChartData = useMemo(() => {
    if (!pricingData) return [];
    const seatCount = metricsPathway?.capacity ?? metricsProgram?.seats_available ?? metricsProgram?.cohort_capacity ?? 0;
    return pricingData.components
      .filter((component) => component.cost_type !== 'group')
      .map((component) => ({
        name: component.name,
        estimated: (component.estimated_amount ?? 0) * (component.cost_type === 'variable' ? seatCount : 1),
        actual: (component.amount ?? 0) * (component.cost_type === 'variable' ? seatCount : 1),
        type: component.cost_type,
      }));
  }, [pricingData, metricsProgram, metricsPathway?.capacity]);

  const pathwayComparisonData = useMemo(() => {
    if (!pricingData) return [];
    return pricingData.break_even.map((row) => ({
      name: row.pathway_name,
      estimated_students: row.estimated_break_even_exact ?? 0,
      actual_students: row.break_even_exact ?? 0,
    }));
  }, [pricingData]);

  const componentEstimatedSlices = useMemo(() => (
    componentChartData
      .filter((item) => item.estimated > 0)
      .map((item) => ({ name: item.name, value: item.estimated }))
  ), [componentChartData]);

  const componentActualSlices = useMemo(() => (
    componentChartData
      .filter((item) => item.actual > 0)
      .map((item) => ({ name: item.name, value: item.actual }))
  ), [componentChartData]);

  const pathwayPieData = useMemo(() => (
    pathwayComparisonData
      .map((row) => ({
        name: row.name,
        value: row.actual_students || row.estimated_students || 0,
      }))
      .filter((row) => row.value > 0)
  ), [pathwayComparisonData]);

  const pathwayPriceData = useMemo(() => (
    metricsTracks
      .filter((pathway) => pathway.id != null)
      .map((pathway) => ({
        name: pathway.name,
        estimated: pathway.estimated_selling_price ?? 0,
        actual: pathway.selling_price ?? 0,
      })) ?? []
  ), [metricsTracks]);

  useEffect(() => {
    if (modalMode !== 'view' || !selectedProgram) {
      return;
    }
    setViewPricingLoading(true);
    programService.getProgramPricing(selectedProgram.id)
      .catch(() => {
        // Ignore view pricing errors for now.
      })
      .finally(() => {
        setViewPricingLoading(false);
      });
  }, [modalMode, selectedProgram]);

  useEffect(() => {
    if (!selectedProgram?.id || modalMode === null) return;
    fetchCohorts(selectedProgram.id);
  }, [selectedProgram?.id, modalMode, fetchCohorts]);

  useEffect(() => {
    if (modalMode !== 'edit' || (buildStep !== 3 && buildStep !== 4 && buildStep !== 5) || !selectedProgram?.id) return;
    if (pricingDraft?.program_id === selectedProgram.id) return;
    let active = true;
    programService.getProgramPricing(selectedProgram.id)
      .then((data) => {
        if (!active) return;
        setPricingDraft(data);
      })
      .catch(() => {
        if (!active) return;
        setPricingDraft(null);
      })
      .finally(() => {
        if (!active) return;
      });
    return () => {
      active = false;
    };
  }, [modalMode, buildStep, selectedProgram?.id, pricingDraft?.program_id]);

  const profitTableRows = useMemo(() => {
    if (!metricsPathway || !metricsBreakEven || !metricsProgram) return [];
    const pickMetricValue = (actual?: number | null, estimated?: number | null) => {
      if (actual != null) return actual;
      if (estimated != null) return estimated;
      return null;
    };
    const sellingPrice = pickMetricValue(metricsPathway.selling_price, metricsPathway.estimated_selling_price);
    const variableCost = pickMetricValue(metricsBreakEven.variable_cost, metricsBreakEven.estimated_variable_cost);
    const fixedCost = pickMetricValue(metricsBreakEven.fixed_cost, metricsBreakEven.estimated_fixed_cost);
    if (sellingPrice == null || variableCost == null || fixedCost == null) return [];
    const seats = metricsPathway.capacity ?? metricsProgram.seats_available ?? metricsProgram.cohort_capacity ?? null;
    const rowCount = seats && seats > 0 ? seats : 0;
    const cmUnit = sellingPrice - variableCost;
    return Array.from({ length: rowCount }, (_, index) => {
      const count = index + 1;
      const revenue = sellingPrice * count;
      const vcTotal = variableCost * count;
      const profit = revenue - vcTotal - fixedCost;
      return {
        count,
        sellingPrice,
        revenue,
        variableCost,
        vcTotal,
        fixedCost,
        cmUnit,
        profit,
      };
    });
  }, [metricsPathway, metricsBreakEven, metricsProgram]);

  if (!user?.is_admin) {
    return (
      <div className="space-y-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-600">Access denied. Admin privileges required.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="mb-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Programs Management</h1>
            <p className="text-sm text-gray-600 dark:text-slate-300 mt-1">Create, update, and analyze program offerings.</p>
          </div>
          <button
            onClick={openCreateModal}
            className="inline-flex items-center justify-center gap-2 font-semibold rounded-lg transition-colors bg-emerald-600 text-white border border-emerald-600 hover:bg-emerald-700 hover:border-emerald-700 px-4 py-2"
          >
            <PlusIcon className="w-5 h-5" />
            Add Program
          </button>
        </div>
      </div>

      {(error || metricsError) && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 text-red-600 space-y-1">
          {error && <p>{error}</p>}
          {metricsError && <p>{metricsError}</p>}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <div className="bg-white/85 dark:bg-slate-900/70 border border-slate-100/90 dark:border-slate-700/80 rounded-3xl shadow-[0_14px_36px_rgba(15,23,42,0.12)] dark:shadow-[0_18px_40px_rgba(0,0,0,0.55)] backdrop-blur-[18px] overflow-hidden p-5">
          <p className="text-sm uppercase tracking-wide text-gray-500">Active Programs</p>
          <p className="text-4xl font-bold text-gray-900 mt-2">{activePrograms}</p>
          <p className="mt-1 text-gray-500">{totalPrograms} total offerings</p>
        </div>
        <div className="bg-white/85 dark:bg-slate-900/70 border border-slate-100/90 dark:border-slate-700/80 rounded-3xl shadow-[0_14px_36px_rgba(15,23,42,0.12)] dark:shadow-[0_18px_40px_rgba(0,0,0,0.55)] backdrop-blur-[18px] overflow-hidden p-5">
          <p className="text-sm text-gray-500 uppercase tracking-wide">All Programs</p>
          <p className="text-4xl font-bold text-gray-900 mt-2">{totalPrograms}</p>
          <p className="text-gray-500 mt-1">Including paused/inactive programs</p>
        </div>
        <div className="bg-white/85 dark:bg-slate-900/70 border border-slate-100/90 dark:border-slate-700/80 rounded-3xl shadow-[0_14px_36px_rgba(15,23,42,0.12)] dark:shadow-[0_18px_40px_rgba(0,0,0,0.55)] backdrop-blur-[18px] overflow-hidden p-5">
          <p className="text-sm text-gray-500 uppercase tracking-wide">Students Enrolled</p>
          <div className="flex items-baseline gap-2 mt-2">
            <p className="text-4xl font-bold text-gray-900">{metricsLoading ? '–' : enrolledCount}</p>
            <span className="text-xs text-gray-500">Active Student stage</span>
          </div>
        </div>
      </div>

      <div className="bg-white/85 dark:bg-slate-900/70 border border-slate-100/90 dark:border-slate-700/80 rounded-3xl shadow-[0_14px_36px_rgba(15,23,42,0.12)] dark:shadow-[0_18px_40px_rgba(0,0,0,0.55)] backdrop-blur-[18px] overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100/80 dark:border-slate-700/80 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-medium text-slate-900 dark:text-slate-50">Programs</h2>
            <p className="text-sm text-gray-500">Click any row to view program details.</p>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-slate-700/60">
            <thead className="bg-gray-50 dark:bg-slate-900">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-slate-200 uppercase tracking-wider">Title</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-slate-200 uppercase tracking-wider">Location</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-slate-200 uppercase tracking-wider">Duration</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-slate-200 uppercase tracking-wider">Seats Available</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-slate-200 uppercase tracking-wider">Students Enrolled</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-slate-200 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-slate-200 uppercase tracking-wider">Host Universities</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-slate-200 uppercase tracking-wider">Last Updated</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-slate-200 uppercase tracking-wider">Metrics</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-slate-200 uppercase tracking-wider">Builder</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-slate-900/60 divide-y divide-gray-200 dark:divide-slate-700/60 text-sm">
              {loading ? (
                <tr>
                  <td colSpan={10} className="px-4 py-6 text-center text-gray-500">Loading programs...</td>
                </tr>
              ) : programs.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-4 py-6 text-center text-gray-500">No programs found.</td>
                </tr>
              ) : (
                programs.map(program => {
                  const hostUniversities = getHostUniversities(program);
                  const cohortMeta = programCohortMeta[program.id];
                  const durationLabel = cohortMeta?.durationDays ? formatDurationDays(cohortMeta.durationDays) : (program.duration || 'TBD');
                  const seatsAvailableLabel = cohortMeta?.seatsAvailable != null
                    ? cohortMeta.seatsAvailable
                    : (program.seats_available ?? program.cohort_capacity ?? 'Capacity TBD');
                  return (
                    <tr
                      key={program.id}
                      className="hover:bg-gray-50 dark:hover:bg-slate-800/60 cursor-pointer"
                      onClick={() => openProgramModal(program)}
                    >
                      <td className="px-4 py-3 font-medium text-gray-900">{program.title}</td>
                      <td className="px-4 py-3">{program.location || '—'}</td>
                      <td className="px-4 py-3">{durationLabel}</td>
                      <td className="px-4 py-3">
                        {seatsAvailableLabel}
                      </td>
                      <td className="px-4 py-3 text-gray-900">{program.enrolled_student_count ?? 0}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-0.5 text-xs rounded-full ${
                          program.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {program.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-3">{hostUniversities.length > 0 ? hostUniversities.join(', ') : 'Not assigned'}</td>
                      <td className="px-4 py-3 text-gray-500">
                        {formatDate(program.updated_at || program.created_at)}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            openMetricsModal(program);
                          }}
                          className="inline-flex items-center justify-center gap-2 font-semibold rounded-lg transition-colors bg-white/80 dark:bg-slate-900/60 border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 px-2 py-1 text-xs"
                        >
                          View
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            openBuilderModal(program);
                          }}
                          className="inline-flex items-center justify-center gap-2 font-semibold rounded-lg transition-colors bg-white/80 dark:bg-slate-900/60 border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 px-2 py-1 text-xs"
                        >
                          Builder
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 z-40 flex items-center justify-center">
          <div className="absolute inset-0 bg-slate-900/50" onClick={closeModal} />
          <div className="bg-white/85 dark:bg-slate-900/70 border border-slate-100/90 dark:border-slate-700/80 rounded-3xl shadow-[0_14px_36px_rgba(15,23,42,0.12)] dark:shadow-[0_18px_40px_rgba(0,0,0,0.55)] backdrop-blur-[18px] overflow-hidden overflow-y-auto overflow-x-hidden relative z-50 max-w-3xl w-full mx-4 max-h-[90vh]">
            <div className="flex items-start justify-between px-6 py-4 border-b border-slate-200/70 dark:border-slate-700/70">
              <div>
                <p className="text-sm uppercase tracking-wide text-slate-500 dark:text-slate-300">
                  {modalMode === 'create' ? 'Create Program' : 'Program Details'}
                </p>
                <h3 className="text-2xl font-semibold text-slate-900 dark:text-white">
                  {modalMode === 'create' ? 'New Program' : selectedProgram?.title}
                </h3>
              </div>
              <button onClick={closeModal} className="text-slate-500 hover:text-slate-700 dark:text-slate-300 dark:hover:text-slate-100">
                ✕
              </button>
            </div>

            <div className="px-6 py-6">
              {(modalMode === 'view' && selectedProgram) && (
                <div className="space-y-4 text-slate-700 dark:text-slate-200">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs uppercase text-slate-500 dark:text-slate-300">Location</p>
                      <p className="text-base font-medium">{selectedProgram.location || 'TBD'}</p>
                    </div>
                    <div>
                      <p className="text-xs uppercase text-slate-500 dark:text-slate-300">Duration</p>
                      <p className="text-base font-medium">{selectedProgram.duration || 'TBD'}</p>
                    </div>
                    <div>
                      <p className="text-xs uppercase text-slate-500 dark:text-slate-300">Host Universities</p>
                      <p className="text-base font-medium">
                        {getHostUniversities(selectedProgram).length > 0 ? getHostUniversities(selectedProgram).join(', ') : 'Not assigned'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs uppercase text-slate-500 dark:text-slate-300">Status</p>
                      <span className={`inline-flex px-3 py-1 rounded-full text-sm font-medium ${
                        selectedProgram.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {selectedProgram.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <div>
                      <p className="text-xs uppercase text-slate-500 dark:text-slate-300">Featured</p>
                      <p className="text-base font-medium">{selectedProgram.is_featured ? 'Yes' : 'No'}</p>
                    </div>
                  </div>

                  {selectedProgram.description && (
                    <div>
                      <p className="text-xs uppercase text-slate-500 dark:text-slate-300 mb-1">Description</p>
                      <p>{selectedProgram.description}</p>
                    </div>
                  )}

                  {selectedProgram.tags?.length ? (
                    <div>
                      <p className="text-xs uppercase text-slate-500 dark:text-slate-300 mb-1">Tags</p>
                      <p>
                        {[
                          selectedProgram.tags?.length ? selectedProgram.tags.join(', ') : null
                        ].filter(Boolean).join(' · ')}
                      </p>
                    </div>
                  ) : null}

                  {selectedProgram.highlights.length > 0 && (
                    <div>
                      <p className="text-xs uppercase text-slate-500 dark:text-slate-300 mb-1">Highlights</p>
                      <ul className="list-disc list-inside space-y-1">
                        {selectedProgram.highlights.map((highlight, idx) => (
                          <li key={idx}>{highlight}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {viewPricingLoading ? (
                    <div className="bg-white/85 dark:bg-slate-900/70 border border-slate-100/90 dark:border-slate-700/80 rounded-3xl shadow-[0_14px_36px_rgba(15,23,42,0.12)] dark:shadow-[0_18px_40px_rgba(0,0,0,0.55)] backdrop-blur-[18px] overflow-hidden p-4 text-sm text-slate-500 dark:text-slate-300">
                      Loading tracks…
                    </div>
                  ) : null}

                  <div className="flex justify-between items-center pt-4 border-t border-slate-200/70 dark:border-slate-700/70">
                    <div className="space-x-3">
                      <button
                        type="button"
                        onClick={startEditMode}
                        className="inline-flex items-center justify-center gap-2 font-semibold rounded-lg transition-colors bg-emerald-600 text-white border border-emerald-600 hover:bg-emerald-700 hover:border-emerald-700 px-4 py-2"
                      >
                        Edit Program
                      </button>
                      {selectedProgram?.id && (
                        <button
                          type="button"
                          onClick={startPricingStep}
                          className="inline-flex items-center justify-center gap-2 font-semibold rounded-lg transition-colors bg-white/80 dark:bg-slate-900/60 border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 px-4 py-2"
                        >
                          Tracks & Extensions
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => selectedProgram && handleDelete(selectedProgram)}
                        className="inline-flex items-center justify-center gap-2 font-semibold rounded-lg transition-colors bg-red-500 text-white border border-red-500 hover:bg-red-600 hover:border-red-600 px-4 py-2"
                      >
                        Delete
                      </button>
                    </div>
                    <button
                      type="button"
                      onClick={closeModal}
                      className="text-slate-500 hover:text-slate-700 dark:text-slate-300 dark:hover:text-slate-100"
                    >
                      Close
                    </button>
                  </div>
                </div>
              )}

              {(modalMode === 'create' || modalMode === 'edit') && (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="mb-4">
                    <div className="flex items-center gap-3 text-xs uppercase tracking-wide text-slate-400 dark:text-slate-500">
                      {stepLabels.map((label, index) => {
                        const stepNumber = index + 1;
                        const isActive = buildStep === stepNumber;
                        const isComplete = buildStep > stepNumber;
                        return (
                          <div key={label} className="flex items-center gap-2">
                            <span className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ${
                              isActive
                                ? 'bg-emerald-600 text-white'
                                : isComplete
                                  ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200'
                                  : 'bg-slate-100 text-slate-400 dark:bg-slate-800 dark:text-slate-400'
                            }`}>
                              {stepNumber}
                            </span>
                            <span className={isActive ? 'text-slate-900 dark:text-white' : 'text-slate-400 dark:text-slate-500'}>{label}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {buildStep === 1 && (
                    <div className="space-y-6">
                      {/* CIUE: CARES data capture fields */}
                      <div>
                    <h4 className={sectionTitleClassName}>Basics</h4>
                    <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="md:col-span-2">
                        <label className={labelClassName}>
                          Title *
                        </label>
                        <input
                          type="text"
                          name="title"
                          aria-label="Title"
                          value={formData.title}
                          onChange={handleInputChange}
                          required
                          className={inputClassName}
                        />
                      </div>
                      <div>
                        <label className={labelClassName}>Location</label>
                        <input
                          type="text"
                          name="location"
                          value={formData.location}
                          onChange={handleInputChange}
                          className={inputClassName}
                        />
                      </div>
                      <div>
                        <label className={labelClassName}>Duration</label>
                        <input
                          type="text"
                          name="duration"
                          value={formData.duration}
                          onChange={handleInputChange}
                          className={inputClassName}
                        />
                      </div>
                      <label className="inline-flex items-center gap-2 pt-7">
                        <input
                          type="checkbox"
                          name="is_featured"
                          checked={formData.is_featured}
                          onChange={handleInputChange}
                          className="rounded border-slate-300 dark:border-slate-600 text-emerald-600 focus:ring-emerald-500/50"
                        />
                        <span className="text-sm text-slate-700 dark:text-slate-200">Featured</span>
                      </label>
                      <label className="inline-flex items-center gap-2 pt-7">
                        <input
                          type="checkbox"
                          name="is_active"
                          checked={formData.is_active}
                          onChange={handleInputChange}
                          className="rounded border-slate-300 dark:border-slate-600 text-emerald-600 focus:ring-emerald-500/50"
                        />
                        <span className="text-sm text-slate-700 dark:text-slate-200">Active</span>
                      </label>
                    </div>
                  </div>
                    </div>
                  )}

                  {buildStep === 2 && (
                    <div className="space-y-6">
                  <div>
                    <h4 className={sectionTitleClassName}>Tags</h4>
                    <div className="mt-3">
                      <label className={labelClassName}>Tags / Categories</label>
                      <input
                        type="text"
                        name="tags"
                        value={formData.tags}
                        onChange={handleInputChange}
                        placeholder="Arabic, Islamic Studies"
                        className={inputClassName}
                      />
                    </div>
                  </div>

                  <div>
                    <h4 className={sectionTitleClassName}>Host Universities</h4>
                    <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                      {hostUniversities.map((partner) => {
                        const checked = selectedHostUniversityIds.includes(partner.id);
                        return (
                          <label key={partner.id} className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={(event) => {
                                const isChecked = event.target.checked;
                                setSelectedHostUniversityIds((prev) => (
                                  isChecked ? [...prev, partner.id] : prev.filter((id) => id !== partner.id)
                                ));
                              }}
                              className="rounded border-slate-300 dark:border-slate-600 text-emerald-600 focus:ring-emerald-500/50"
                            />
                            <span>{partner.name}</span>
                          </label>
                        );
                      })}
                      {hostUniversities.length === 0 && (
                        <p className={mutedTextClassName}>No host universities available yet.</p>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className={labelClassName}>
                      Description
                    </label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      rows={3}
                      className={textareaClassName}
                    />
                  </div>

                      <div>
                    <label className={labelClassName}>
                      Highlights
                    </label>
                    {formData.highlights.map((highlight, index) => (
                      <div key={index} className="flex gap-2 mb-2">
                        <input
                          type="text"
                          value={highlight}
                          onChange={(e) => handleHighlightChange(index, e.target.value)}
                          className={`flex-1 ${inputClassName}`}
                          placeholder="Program highlight"
                        />
                        <button
                          type="button"
                          onClick={() => removeHighlight(index)}
                          className="px-3 py-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"
                        >
                          <TrashIcon className="w-5 h-5" />
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={addHighlight}
                      className="text-emerald-600 hover:text-emerald-700 dark:text-emerald-300 dark:hover:text-emerald-200 text-sm"
                    >
                      + Add Highlight
                    </button>
                  </div>

                      <div>
                    <label className={labelClassName}>
                      Image URL
                    </label>
                    <input
                      type="url"
                      name="image_url"
                      value={formData.image_url}
                      onChange={handleInputChange}
                      className={inputClassName}
                    />
                  </div>
                      {selectedProgram?.id && (
                        <div className="rounded-lg border border-emerald-100 bg-emerald-50 dark:border-emerald-900/40 dark:bg-emerald-900/20 p-4 text-sm text-emerald-800 dark:text-emerald-200">
                          Ready to build tracks & extensions?{' '}
                          <button
                            type="button"
                            onClick={() => setBuildStep(3)}
                            className="font-semibold underline"
                          >
                            Open track builder
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {buildStep === 3 && (
                    <div className="space-y-4">
                      <div className="bg-white/85 dark:bg-slate-900/70 border border-slate-100/90 dark:border-slate-700/80 rounded-3xl shadow-[0_14px_36px_rgba(15,23,42,0.12)] dark:shadow-[0_18px_40px_rgba(0,0,0,0.55)] backdrop-blur-[18px] overflow-hidden p-4 space-y-3">
                        <div>
                          <h4 className={sectionTitleClassName}>Bundle Discount</h4>
                          <p className={`${mutedTextClassName} mt-1`}>Optional discount when students combine cohorts or extensions.</p>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <label className={smallLabelClassName}>
                              Discount Amount ({bundleCurrency})
                            </label>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              name="bundle_discount_amount"
                              value={formData.bundle_discount_amount}
                              onChange={handleInputChange}
                              className={inputClassName}
                              placeholder="0.00"
                            />
                          </div>
                          <div className="md:col-span-2">
                            <label className={smallLabelClassName}>
                              Discount Description
                            </label>
                            <input
                              type="text"
                              name="bundle_discount_description"
                              value={formData.bundle_discount_description}
                              onChange={handleInputChange}
                              className={inputClassName}
                              placeholder="Applies when students select multiple cohorts"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="bg-white/85 dark:bg-slate-900/70 border border-slate-100/90 dark:border-slate-700/80 rounded-3xl shadow-[0_14px_36px_rgba(15,23,42,0.12)] dark:shadow-[0_18px_40px_rgba(0,0,0,0.55)] backdrop-blur-[18px] overflow-hidden p-4 space-y-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className={sectionTitleClassName}>Cohorts</h4>
                            <p className={`${mutedTextClassName} mt-1`}>Add as many cohorts (tracks) as you need.</p>
                          </div>
                          <button
                            type="button"
                            onClick={addCohort}
                            className="rounded-lg border border-slate-300/70 dark:border-slate-700/70 px-3 py-1.5 text-sm text-slate-700 dark:text-slate-200 hover:border-slate-400/70 dark:hover:border-slate-500/70"
                          >
                            + Add Cohort
                          </button>
                        </div>

                        {cohorts.length === 0 && (
                          <p className={mutedTextClassName}>No cohorts yet.</p>
                        )}

                        {cohorts.map((cohort, index) => (
                          <div key={cohort.id ?? `cohort-${index}`} className="rounded-lg border border-slate-200/70 dark:border-slate-700/70 bg-white/70 dark:bg-slate-900/60 p-4 space-y-3">
                            <div className="flex items-center justify-between">
                              <p className="text-sm font-semibold text-slate-900 dark:text-white">{cohort.name || `Cohort ${index + 1}`}</p>
                              <button
                                type="button"
                                onClick={() => removeCohort(index)}
                                className="text-sm text-red-600 hover:text-red-700 dark:hover:text-red-500"
                              >
                                Remove
                              </button>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              <div>
                                <label className={smallLabelClassName}>Name</label>
                                <input
                                  type="text"
                                  value={cohort.name}
                                  onChange={(event) => updateCohortField(index, 'name', event.target.value)}
                                  className={inputClassName}
                                />
                              </div>
                              <div>
                                <label className={smallLabelClassName}>Status</label>
                                <select
                                  value={cohort.status || 'OPEN'}
                                  onChange={(event) => updateCohortField(index, 'status', event.target.value)}
                                  className={selectClassName}
                                >
                                  <option value="OPEN">Open</option>
                                  <option value="CLOSED">Closed</option>
                                  <option value="FULL">Full</option>
                                  <option value="ARCHIVED">Archived</option>
                                </select>
                              </div>
                              <div>
                                <label className={smallLabelClassName}>Start Date</label>
                                <input
                                  type="date"
                                  value={cohort.starts_on || ''}
                                  onChange={(event) => updateCohortField(index, 'starts_on', event.target.value || null)}
                                  className={inputClassName}
                                />
                              </div>
                              <div>
                                <label className={smallLabelClassName}>End Date</label>
                                <input
                                  type="date"
                                  value={cohort.ends_on || ''}
                                  onChange={(event) => updateCohortField(index, 'ends_on', event.target.value || null)}
                                  className={inputClassName}
                                />
                              </div>
                              <div>
                                <label className={smallLabelClassName}>Application Deadline</label>
                                <input
                                  type="date"
                                  value={cohort.application_deadline || ''}
                                  onChange={(event) => updateCohortField(index, 'application_deadline', event.target.value || null)}
                                  className={inputClassName}
                                />
                              </div>
                              <div>
                                <label className={smallLabelClassName}>Capacity</label>
                                <input
                                  type="number"
                                  min="0"
                                  value={cohort.capacity ?? ''}
                                  onChange={(event) => updateCohortField(index, 'capacity', event.target.value ? Number(event.target.value) : null)}
                                  className={inputClassName}
                                />
                              </div>
                              <div>
                                <label className={smallLabelClassName}>Seats Available</label>
                                <input
                                  type="number"
                                  min="0"
                                  value={cohort.seats_available ?? ''}
                                  onChange={(event) => updateCohortField(index, 'seats_available', event.target.value ? Number(event.target.value) : null)}
                                  className={inputClassName}
                                />
                              </div>
                              <div>
                                <label className={smallLabelClassName}>Selling Price</label>
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={cohort.selling_price ?? ''}
                                  onChange={(event) => updateCohortField(index, 'selling_price', event.target.value ? Number(event.target.value) : null)}
                                  className={inputClassName}
                                />
                              </div>
                              <div className="md:col-span-2">
                                <label className={smallLabelClassName}>Classes Offered</label>
                                <textarea
                                  rows={3}
                                  value={(cohort.classes_offered || []).join('\n')}
                                  onChange={(event) => updateCohortField(
                                    index,
                                    'classes_offered',
                                    event.target.value
                                      .split(/[\n,]/g)
                                      .map((item) => item.trim())
                                      .filter(Boolean)
                                  )}
                                  placeholder="One class per line or comma-separated"
                                  className={textareaClassName}
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      {selectedProgram?.id ? (
                        <ProgramPricingManagement
                          embedded
                          hideProgramSelect
                          initialProgramId={selectedProgram.id}
                          showBundleDiscount={false}
                          bundleDiscountAmountOverride={bundleDiscountAmountValue}
                          bundleDiscountDescriptionOverride={formData.bundle_discount_description}
                          cohortOverrides={cohorts
                            .filter((cohort) => cohort.id != null)
                            .map((cohort) => ({
                              id: cohort.id as number,
                              name: cohort.name,
                              start_date: cohort.starts_on ?? null,
                              end_date: cohort.ends_on ?? null,
                              selling_price: cohort.selling_price ?? null,
                              estimated_selling_price: cohort.selling_price ?? null,
                            }))}
                          onPricingChange={setPricingDraft}
                        />
                      ) : (
                        <div className="rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-900/50 dark:bg-amber-900/20 p-4 text-sm text-amber-800 dark:text-amber-200">
                          Save the program first to unlock tracks & extensions.
                        </div>
                      )}

                      {selectedProgram?.id && (
                        <div className="flex justify-end">
                          <button
                            type="button"
                            onClick={handleCohortSave}
                            disabled={cohortSaving}
                            className="inline-flex items-center justify-center gap-2 font-semibold rounded-lg transition-colors bg-emerald-600 text-white border border-emerald-600 hover:bg-emerald-700 hover:border-emerald-700 px-4 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-70"
                          >
                            {cohortSaving ? 'Saving…' : 'Save Cohorts'}
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {buildStep === 4 && (
                    <div className="space-y-6">
                      <div>
                        <h4 className={sectionTitleClassName}>Program Portal Content</h4>
                        <p className={`${mutedTextClassName} mt-1`}>These details power the student-facing program overview in Stage 2.</p>
                      </div>

                      <div>
                        <p className={sectionTitleClassName}>Program Overview</p>
                        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className={labelClassName}>Slug</label>
                            <input
                              type="text"
                              name="slug"
                              value={formData.slug}
                              onChange={handleInputChange}
                              placeholder="program-slug"
                              className={inputClassName}
                            />
                          </div>
                          <div>
                            <label className={labelClassName}>Short Tagline</label>
                            <input
                              type="text"
                              name="short_tagline"
                              value={formData.short_tagline}
                              onChange={handleInputChange}
                              placeholder="A concise one-line hook"
                              className={inputClassName}
                            />
                          </div>
                          <div className="md:col-span-2">
                            <label className={labelClassName}>Overview</label>
                            <textarea
                              name="overview"
                              value={formData.overview}
                              onChange={handleInputChange}
                              rows={3}
                              placeholder="High-level program overview"
                              className={textareaClassName}
                            />
                          </div>
                          <div className="md:col-span-2">
                            <label className={labelClassName}>Mission Statement</label>
                            <textarea
                              name="mission_statement"
                              value={formData.mission_statement}
                              onChange={handleInputChange}
                              rows={3}
                              placeholder="Program mission or purpose"
                              className={textareaClassName}
                            />
                          </div>
                          <div>
                            <label className={labelClassName}>Primary City</label>
                            <input
                              type="text"
                              name="primary_city"
                              value={formData.primary_city}
                              onChange={handleInputChange}
                              className={inputClassName}
                            />
                          </div>
                          <div>
                            <label className={labelClassName}>Primary Country</label>
                            <input
                              type="text"
                              name="primary_country"
                              value={formData.primary_country}
                              onChange={handleInputChange}
                              className={inputClassName}
                            />
                          </div>
                          <div>
                            <label className={labelClassName}>Hero Image URL</label>
                            <input
                              type="text"
                              name="hero_image_url"
                              value={formData.hero_image_url}
                              onChange={handleInputChange}
                              placeholder="https://..."
                              className={inputClassName}
                            />
                          </div>
                          <div>
                            <label className={labelClassName}>Gallery Image URLs</label>
                            <textarea
                              name="gallery_image_urls"
                              value={formData.gallery_image_urls}
                              onChange={handleInputChange}
                              rows={3}
                              placeholder="One URL per line"
                              className={textareaClassName}
                            />
                          </div>
                        </div>
                      </div>

                      <div>
                        <p className={sectionTitleClassName}>Why This Program Exists</p>
                        <div className="mt-3 space-y-4">
                          <div>
                            <label className={labelClassName}>Portal Summary</label>
                            <textarea
                              name="portal_summary"
                              value={formData.portal_summary}
                              onChange={handleInputChange}
                              rows={3}
                              placeholder="1-2 sentence snapshot of the program, outcomes, and who it is for."
                              className={textareaClassName}
                            />
                          </div>

                          <div>
                            <label className={labelClassName}>Why Study Abroad (Benefits)</label>
                            <textarea
                              name="portal_benefits"
                              value={formData.portal_benefits}
                              onChange={handleInputChange}
                              rows={4}
                              placeholder="One benefit per line (confidence, academic growth, global network, etc.)"
                              className={textareaClassName}
                            />
                          </div>
                        </div>
                      </div>

                      <div>
                        <p className={sectionTitleClassName}>Academic Experience</p>
                        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className={labelClassName}>Academic Focus</label>
                            <textarea
                              name="academic_focus"
                              value={formData.academic_focus}
                              onChange={handleInputChange}
                              rows={3}
                              className={textareaClassName}
                            />
                          </div>
                          <div>
                            <label className={labelClassName}>Language of Instruction</label>
                            <input
                              type="text"
                              name="language_of_instruction"
                              value={formData.language_of_instruction}
                              onChange={handleInputChange}
                              className={inputClassName}
                            />
                          </div>
                          <div>
                            <label className={labelClassName}>Language Level Required</label>
                            <input
                              type="text"
                              name="language_level_required"
                              value={formData.language_level_required}
                              onChange={handleInputChange}
                              className={inputClassName}
                            />
                          </div>
                          <div className="md:col-span-2">
                            <label className={labelClassName}>Academic Format</label>
                            <textarea
                              name="academic_format"
                              value={formData.academic_format}
                              onChange={handleInputChange}
                              rows={3}
                              className={textareaClassName}
                            />
                          </div>
                          <div className="md:col-span-2">
                            <label className={labelClassName}>Credit or Certificate Info</label>
                            <textarea
                              name="credit_or_certificate_info"
                              value={formData.credit_or_certificate_info}
                              onChange={handleInputChange}
                              rows={3}
                              className={textareaClassName}
                            />
                          </div>
                          <div className="md:col-span-2">
                            <label className={labelClassName}>Academic Narrative</label>
                            <textarea
                              name="portal_academics"
                              value={formData.portal_academics}
                              onChange={handleInputChange}
                              rows={4}
                              placeholder="Curriculum, credits, faculty highlights, learning outcomes."
                              className={textareaClassName}
                            />
                          </div>
                        </div>
                      </div>

                      <div>
                        <p className={sectionTitleClassName}>Life on the Ground</p>
                        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className={labelClassName}>Housing Type</label>
                            <input
                              type="text"
                              name="housing_type"
                              value={formData.housing_type}
                              onChange={handleInputChange}
                              className={inputClassName}
                            />
                          </div>
                          <div>
                            <label className={labelClassName}>Housing Description</label>
                            <textarea
                              name="housing_description"
                              value={formData.housing_description}
                              onChange={handleInputChange}
                              rows={3}
                              className={textareaClassName}
                            />
                          </div>
                          <div>
                            <label className={labelClassName}>Food Description</label>
                            <textarea
                              name="food_description"
                              value={formData.food_description}
                              onChange={handleInputChange}
                              rows={3}
                              className={textareaClassName}
                            />
                          </div>
                          <div className="flex items-center gap-2 pt-7">
                            <input
                              type="checkbox"
                              name="meals_included"
                              checked={formData.meals_included}
                              onChange={handleInputChange}
                              className="rounded border-slate-300 dark:border-slate-600 text-emerald-600 focus:ring-emerald-500/50"
                            />
                            <span className="text-sm text-slate-700 dark:text-slate-200">Meals included</span>
                          </div>
                          <div>
                            <label className={labelClassName}>Prayer Environment</label>
                            <textarea
                              name="prayer_environment"
                              value={formData.prayer_environment}
                              onChange={handleInputChange}
                              rows={3}
                              className={textareaClassName}
                            />
                          </div>
                          <div className="md:col-span-2">
                            <label className={labelClassName}>Community Life Description</label>
                            <textarea
                              name="community_life_description"
                              value={formData.community_life_description}
                              onChange={handleInputChange}
                              rows={3}
                              className={textareaClassName}
                            />
                          </div>
                          <div className="md:col-span-2">
                            <label className={labelClassName}>Safety Overview</label>
                            <textarea
                              name="safety_overview"
                              value={formData.safety_overview}
                              onChange={handleInputChange}
                              rows={3}
                              className={textareaClassName}
                            />
                          </div>
                        </div>
                      </div>

                      <div>
                        <p className={sectionTitleClassName}>CIUE CARES Details</p>
                        <p className={`${mutedTextClassName} mt-1`}>
                          Capture Community, Accommodation, Representation, Expectations, and Sensitivity details for the program.
                        </p>
                        <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
                          <div className="rounded-2xl border border-slate-200/80 dark:border-slate-700/80 bg-white/80 dark:bg-slate-900/60 p-4 space-y-3">
                            <p className={smallLabelClassName}>Housing & Accommodations</p>
                            <div>
                              <label className={labelClassName}>Housing Location</label>
                              <input
                                type="text"
                                value={ciueProfile.housing_location || ''}
                                onChange={(event) => updateCiueField('housing_location', event.target.value)}
                                className={inputClassName}
                              />
                            </div>
                            <div>
                              <label className={labelClassName}>Room Arrangement</label>
                              <input
                                type="text"
                                value={ciueProfile.room_arrangement || ''}
                                onChange={(event) => updateCiueField('room_arrangement', event.target.value)}
                                className={inputClassName}
                              />
                            </div>
                            <div>
                              <label className={labelClassName}>Nearby Facilities</label>
                              <textarea
                                rows={2}
                                value={ciueProfile.nearby_facilities || ''}
                                onChange={(event) => updateCiueField('nearby_facilities', event.target.value)}
                                className={textareaClassName}
                              />
                            </div>
                            <div>
                              <label className={labelClassName}>Housing Support</label>
                              <textarea
                                rows={2}
                                value={ciueProfile.housing_support || ''}
                                onChange={(event) => updateCiueField('housing_support', event.target.value)}
                                className={textareaClassName}
                              />
                            </div>
                            <div>
                              <label className={labelClassName}>Prayer Access Details</label>
                              <textarea
                                rows={2}
                                value={ciueProfile.prayer_access || ''}
                                onChange={(event) => updateCiueField('prayer_access', event.target.value)}
                                className={textareaClassName}
                              />
                            </div>
                          </div>

                          <div className="rounded-2xl border border-slate-200/80 dark:border-slate-700/80 bg-white/80 dark:bg-slate-900/60 p-4 space-y-3">
                            <p className={smallLabelClassName}>Meals & Food Access</p>
                            <div>
                              <label className={labelClassName}>Meal Plan Details</label>
                              <textarea
                                rows={2}
                                value={ciueProfile.meal_plan_details || ''}
                                onChange={(event) => updateCiueField('meal_plan_details', event.target.value)}
                                className={textareaClassName}
                              />
                            </div>
                            <div>
                              <label className={labelClassName}>Halal Food Availability</label>
                              <textarea
                                rows={2}
                                value={ciueProfile.halal_food_access || ''}
                                onChange={(event) => updateCiueField('halal_food_access', event.target.value)}
                                className={textareaClassName}
                              />
                            </div>
                            <div>
                              <label className={labelClassName}>Grocery Access</label>
                              <textarea
                                rows={2}
                                value={ciueProfile.grocery_access || ''}
                                onChange={(event) => updateCiueField('grocery_access', event.target.value)}
                                className={textareaClassName}
                              />
                            </div>
                          </div>

                          <div className="rounded-2xl border border-slate-200/80 dark:border-slate-700/80 bg-white/80 dark:bg-slate-900/60 p-4 space-y-3">
                            <p className={smallLabelClassName}>Community & Student Life</p>
                            <div>
                              <label className={labelClassName}>Muslim Community Presence</label>
                              <textarea
                                rows={2}
                                value={ciueProfile.muslim_community_presence || ''}
                                onChange={(event) => updateCiueField('muslim_community_presence', event.target.value)}
                                className={textareaClassName}
                              />
                            </div>
                            <div>
                              <label className={labelClassName}>Student Organizations / Halaqas</label>
                              <textarea
                                rows={2}
                                value={ciueProfile.student_orgs || ''}
                                onChange={(event) => updateCiueField('student_orgs', event.target.value)}
                                className={textareaClassName}
                              />
                            </div>
                            <div>
                              <label className={labelClassName}>Social Support & Belonging</label>
                              <textarea
                                rows={2}
                                value={ciueProfile.social_support || ''}
                                onChange={(event) => updateCiueField('social_support', event.target.value)}
                                className={textareaClassName}
                              />
                            </div>
                          </div>

                          <div className="rounded-2xl border border-slate-200/80 dark:border-slate-700/80 bg-white/80 dark:bg-slate-900/60 p-4 space-y-3">
                            <p className={smallLabelClassName}>Program Expectations</p>
                            <div>
                              <label className={labelClassName}>Program Expectations</label>
                              <textarea
                                rows={2}
                                value={ciueProfile.program_expectations || ''}
                                onChange={(event) => updateCiueField('program_expectations', event.target.value)}
                                className={textareaClassName}
                              />
                            </div>
                            <div>
                              <label className={labelClassName}>Prior Islamic Studies Required?</label>
                              <input
                                type="text"
                                value={ciueProfile.prior_islamic_studies || ''}
                                onChange={(event) => updateCiueField('prior_islamic_studies', event.target.value)}
                                className={inputClassName}
                              />
                            </div>
                            <div>
                              <label className={labelClassName}>Arabic Requirement Details</label>
                              <input
                                type="text"
                                value={ciueProfile.arabic_requirement_details || ''}
                                onChange={(event) => updateCiueField('arabic_requirement_details', event.target.value)}
                                className={inputClassName}
                              />
                            </div>
                            <div>
                              <label className={labelClassName}>Expected Conduct & Participation</label>
                              <textarea
                                rows={2}
                                value={ciueProfile.expected_conduct || ''}
                                onChange={(event) => updateCiueField('expected_conduct', event.target.value)}
                                className={textareaClassName}
                              />
                            </div>
                            <div>
                              <label className={labelClassName}>Travel Readiness</label>
                              <textarea
                                rows={2}
                                value={ciueProfile.travel_readiness || ''}
                                onChange={(event) => updateCiueField('travel_readiness', event.target.value)}
                                className={textareaClassName}
                              />
                            </div>
                            <div>
                              <label className={labelClassName}>Application Steps</label>
                              <textarea
                                rows={2}
                                value={ciueProfile.application_steps || ''}
                                onChange={(event) => updateCiueField('application_steps', event.target.value)}
                                className={textareaClassName}
                              />
                            </div>
                            <div>
                              <label className={labelClassName}>Selection Process / Deadlines</label>
                              <textarea
                                rows={2}
                                value={ciueProfile.selection_process || ''}
                                onChange={(event) => updateCiueField('selection_process', event.target.value)}
                                className={textareaClassName}
                              />
                            </div>
                          </div>

                          <div className="rounded-2xl border border-slate-200/80 dark:border-slate-700/80 bg-white/80 dark:bg-slate-900/60 p-4 space-y-3">
                            <p className={smallLabelClassName}>Daily Life</p>
                            <div>
                              <label className={labelClassName}>Daily Schedule Overview</label>
                              <textarea
                                rows={3}
                                value={ciueProfile.daily_schedule_overview || ''}
                                onChange={(event) => updateCiueField('daily_schedule_overview', event.target.value)}
                                className={textareaClassName}
                              />
                            </div>
                          </div>

                          <div className="rounded-2xl border border-slate-200/80 dark:border-slate-700/80 bg-white/80 dark:bg-slate-900/60 p-4 space-y-3">
                            <p className={smallLabelClassName}>Support & Representation</p>
                            <div>
                              <label className={labelClassName}>Inclusion Statement</label>
                              <textarea
                                rows={2}
                                value={ciueProfile.inclusion_statement || ''}
                                onChange={(event) => updateCiueField('inclusion_statement', event.target.value)}
                                className={textareaClassName}
                              />
                            </div>
                            <div>
                              <label className={labelClassName}>Academic Support</label>
                              <textarea
                                rows={2}
                                value={ciueProfile.academic_support || ''}
                                onChange={(event) => updateCiueField('academic_support', event.target.value)}
                                className={textareaClassName}
                              />
                            </div>
                            <div>
                              <label className={labelClassName}>Cultural Support</label>
                              <textarea
                                rows={2}
                                value={ciueProfile.cultural_support || ''}
                                onChange={(event) => updateCiueField('cultural_support', event.target.value)}
                                className={textareaClassName}
                              />
                            </div>
                            <div>
                              <label className={labelClassName}>Spiritual Support</label>
                              <textarea
                                rows={2}
                                value={ciueProfile.spiritual_support || ''}
                                onChange={(event) => updateCiueField('spiritual_support', event.target.value)}
                                className={textareaClassName}
                              />
                            </div>
                            <div>
                              <label className={labelClassName}>Logistical Support</label>
                              <textarea
                                rows={2}
                                value={ciueProfile.logistical_support || ''}
                                onChange={(event) => updateCiueField('logistical_support', event.target.value)}
                                className={textareaClassName}
                              />
                            </div>
                            <div>
                              <label className={labelClassName}>Representation Commitment</label>
                              <textarea
                                rows={2}
                                value={ciueProfile.representation_commitment || ''}
                                onChange={(event) => updateCiueField('representation_commitment', event.target.value)}
                                className={textareaClassName}
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      <div>
                        <p className={sectionTitleClassName}>Program Structure</p>
                        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className={labelClassName}>Weekly Structure</label>
                            <textarea
                              name="weekly_structure_description"
                              value={formData.weekly_structure_description}
                              onChange={handleInputChange}
                              rows={3}
                              className={textareaClassName}
                            />
                          </div>
                          <div>
                            <label className={labelClassName}>Cultural Immersion</label>
                            <textarea
                              name="cultural_immersion_description"
                              value={formData.cultural_immersion_description}
                              onChange={handleInputChange}
                              rows={3}
                              className={textareaClassName}
                            />
                          </div>
                          <div className="md:col-span-2">
                            <label className={labelClassName}>Excursions Description</label>
                            <textarea
                              name="excursions_description"
                              value={formData.excursions_description}
                              onChange={handleInputChange}
                              rows={3}
                              className={textareaClassName}
                            />
                          </div>
                          <div className="md:col-span-2">
                            <label className={labelClassName}>Core Tracks Overview</label>
                            <textarea
                              name="portal_core_tracks_overview"
                              value={formData.portal_core_tracks_overview}
                              onChange={handleInputChange}
                              rows={3}
                              placeholder="Explain the core tracks, focus areas, and who each track serves."
                              className={textareaClassName}
                            />
                          </div>
                        </div>
                      </div>

                      <div>
                        <p className={sectionTitleClassName}>What’s Included</p>
                        <div className="mt-3">
                          <label className={labelClassName}>Included Features</label>
                          <textarea
                            name="included_features"
                            value={formData.included_features}
                            onChange={handleInputChange}
                            rows={4}
                            placeholder="One item per line (e.g., Tuition, Housing, International Airfare from USA)"
                            className={textareaClassName}
                          />
                        </div>
                      </div>

                      <div>
                        <p className={sectionTitleClassName}>Optional Experiences</p>
                        <div className="mt-3 space-y-4">
                          <div>
                            <label className={labelClassName}>Optional Trips Overview</label>
                            <textarea
                              name="portal_optional_trips_overview"
                              value={formData.portal_optional_trips_overview}
                              onChange={handleInputChange}
                              rows={3}
                              placeholder="Describe optional trips, added value, and what students gain."
                              className={textareaClassName}
                            />
                          </div>
                          <div>
                            <label className={labelClassName}>Optional Trips Description</label>
                            <textarea
                              name="optional_trips_description"
                              value={formData.optional_trips_description}
                              onChange={handleInputChange}
                              rows={3}
                              className={textareaClassName}
                            />
                          </div>
                          <div>
                            <label className={labelClassName}>Optional Courses Description</label>
                            <textarea
                              name="optional_courses_description"
                              value={formData.optional_courses_description}
                              onChange={handleInputChange}
                              rows={3}
                              className={textareaClassName}
                            />
                          </div>
                        </div>
                      </div>

                      <div>
                        <p className={sectionTitleClassName}>Participant Profile</p>
                        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className={labelClassName}>Ideal Participant</label>
                            <textarea
                              name="ideal_participant_description"
                              value={formData.ideal_participant_description}
                              onChange={handleInputChange}
                              rows={3}
                              className={textareaClassName}
                            />
                          </div>
                          <div>
                            <label className={labelClassName}>Not Ideal For</label>
                            <textarea
                              name="not_ideal_for_description"
                              value={formData.not_ideal_for_description}
                              onChange={handleInputChange}
                              rows={3}
                              className={textareaClassName}
                            />
                          </div>
                        </div>
                      </div>

                      <div>
                        <p className={sectionTitleClassName}>FAQ / Next Steps</p>
                        <div className="mt-3">
                          <label className={labelClassName}>FAQ (one Q/A per line)</label>
                          <textarea
                            name="portal_faq"
                            value={formData.portal_faq}
                            onChange={handleInputChange}
                            rows={6}
                            placeholder="Q: What is the daily schedule?\nA: ...\nQ: Can I transfer credits?\nA: ..."
                            className={textareaClassName}
                          />
                        </div>
                      </div>

                    </div>
                  )}

                  {buildStep === 5 && (
                    <div className="space-y-4">
                      <div className="bg-white/85 dark:bg-slate-900/70 border border-slate-100/90 dark:border-slate-700/80 rounded-3xl shadow-[0_14px_36px_rgba(15,23,42,0.12)] dark:shadow-[0_18px_40px_rgba(0,0,0,0.55)] backdrop-blur-[18px] overflow-hidden p-4">
                        <h4 className={sectionTitleClassName}>Break-Even Summary</h4>
                        <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">Break-even calculations appear after pricing is saved.</p>
                        {!(pricingDraft?.break_even?.length) && (
                          <div className={mutedTextClassName + ' mt-3'}>No break-even data yet.</div>
                        )}
                        {(pricingDraft?.break_even?.length || 0) > 0 && (
                          <div className="mt-4 overflow-x-auto">
                            <table className="min-w-full text-sm">
                              <thead className="text-xs uppercase text-slate-500 dark:text-slate-300">
                                <tr>
                                  <th className="py-2 pr-4 text-left">Pathway</th>
                                  <th className="py-2 pr-4 text-left">Break-even (Est)</th>
                                  <th className="py-2 pr-4 text-left">Break-even (Act)</th>
                                  <th className="py-2 pr-4 text-left">Revenue (Est)</th>
                                  <th className="py-2 pr-4 text-left">Revenue (Act)</th>
                                </tr>
                              </thead>
                              <tbody className="text-slate-700 dark:text-slate-200">
                                {pricingDraft?.break_even?.map((row) => (
                                  <tr key={row.pathway_id}>
                                    <td className="py-2 pr-4">{row.pathway_name}</td>
                                    <td className="py-2 pr-4">{row.estimated_break_even_rounded ?? '—'}</td>
                                    <td className="py-2 pr-4">{row.break_even_rounded ?? '—'}</td>
                                    <td className="py-2 pr-4">{formatCurrencyValue(row.estimated_break_even_revenue_exact ?? null, selectedProgram?.currency)}</td>
                                    <td className="py-2 pr-4">{formatCurrencyValue(row.break_even_revenue_exact ?? null, selectedProgram?.currency)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </div>
                  )}


                  <div className="flex justify-end gap-4 pt-4">
                    <button
                      type="button"
                      onClick={() => {
                        if (buildStep === 1) {
                          closeModal();
                        } else {
                          setBuildStep((prev) => Math.max(prev - 1, 1));
                        }
                      }}
                      className="inline-flex items-center justify-center gap-2 font-semibold rounded-lg transition-colors bg-white/80 dark:bg-slate-900/60 border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 px-4 py-2"
                    >
                      {buildStep === 1 ? 'Cancel' : 'Back'}
                    </button>
                    {buildStep < totalSteps ? (
                      <button
                        type="button"
                        onClick={handleStepAdvance}
                        className="inline-flex items-center justify-center gap-2 font-semibold rounded-lg transition-colors bg-emerald-600 text-white border border-emerald-600 hover:bg-emerald-700 hover:border-emerald-700 px-6 py-2"
                      >
                        Next
                      </button>
                    ) : (
                      <button
                        type="submit"
                        className="inline-flex items-center justify-center gap-2 font-semibold rounded-lg transition-colors bg-emerald-600 text-white border border-emerald-600 hover:bg-emerald-700 hover:border-emerald-700 px-6 py-2"
                      >
                        {modalMode === 'edit' ? 'Save Changes' : 'Create Program'}
                      </button>
                    )}
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}

      {showMetricsModal && metricsProgram && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-slate-900/60" onClick={closeMetricsModal} />
          <div className="bg-white/85 dark:bg-slate-900/70 border border-slate-100/90 dark:border-slate-700/80 rounded-3xl shadow-[0_14px_36px_rgba(15,23,42,0.12)] dark:shadow-[0_18px_40px_rgba(0,0,0,0.55)] backdrop-blur-[18px] overflow-hidden overflow-y-auto overflow-x-hidden relative z-50 w-full max-w-6xl mx-4 max-h-[92vh] print-target">
            <div>
              <div className="flex items-start justify-between px-6 py-4 border-b border-slate-200/70 dark:border-slate-700/70">
                <div>
                  <p className="text-sm uppercase tracking-wide text-slate-500 dark:text-slate-300">Program Metrics</p>
                  <h3 className="text-2xl font-semibold text-slate-900 dark:text-white">{metricsProgram.title}</h3>
                </div>
              <div className="flex items-center gap-2 print-hide">
                  <button
                    onClick={handlePrintMetrics}
                    className="rounded-lg border border-slate-200/70 dark:border-slate-700/70 px-3 py-1.5 text-sm text-slate-600 dark:text-slate-200 hover:border-slate-300/70 dark:hover:border-slate-500/70 hover:text-slate-800 dark:hover:text-slate-100"
                  >
                    Print
                  </button>
                  <button onClick={closeMetricsModal} className="text-slate-500 hover:text-slate-700 dark:text-slate-300 dark:hover:text-slate-100">
                    ✕
                  </button>
                </div>
              </div>
              <div className="px-6 py-6 space-y-6">
              {pricingLoading ? (
                <div className="text-slate-500 dark:text-slate-300">Loading metrics…</div>
              ) : !pricingData ? (
                <div className="text-slate-500 dark:text-slate-300">No pricing data yet.</div>
              ) : (
                <>
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <label className="text-sm font-medium text-slate-700 dark:text-slate-200">Study Track</label>
                      <select
                        value={selectedPathwayId ?? ''}
                        onChange={(event) => {
                          const value = event.target.value;
                          setSelectedPathwayId(value ? Number(value) : null);
                        }}
                        className="rounded-lg border border-slate-200/70 dark:border-slate-700/70 bg-white/90 dark:bg-slate-900/60 px-3 py-2 text-sm text-slate-900 dark:text-slate-100"
                      >
                        <option value="">Select track</option>
                        {metricsTracks
                          .filter((pkg) => pkg.id != null)
                          .map((pkg) => (
                            <option key={String(pkg.id)} value={String(pkg.id)}>{pkg.name}</option>
                          ))}
                      </select>
                    </div>
                    <div className="text-sm text-slate-500 dark:text-slate-300">
                      Seats: {metricsProgram.seats_available ?? metricsProgram.cohort_capacity ?? '—'} · Enrolled: {metricsProgram.enrolled_student_count ?? 0}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                    <div className="bg-white/85 dark:bg-slate-900/70 border border-slate-100/90 dark:border-slate-700/80 rounded-3xl shadow-[0_14px_36px_rgba(15,23,42,0.12)] dark:shadow-[0_18px_40px_rgba(0,0,0,0.55)] backdrop-blur-[18px] overflow-hidden p-4">
                      <p className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-300">Selling Price</p>
                      <p className="mt-2 text-lg font-semibold text-slate-900 dark:text-white">
                        Est: {formatCurrencyValue(metricsPathway?.estimated_selling_price ?? null, metricsProgram.currency)}
                      </p>
                      <p className="text-sm text-slate-600 dark:text-slate-300">
                        Act: {formatCurrencyValue(metricsPathway?.selling_price ?? null, metricsProgram.currency)}
                      </p>
                    </div>
                    <div className="bg-white/85 dark:bg-slate-900/70 border border-slate-100/90 dark:border-slate-700/80 rounded-3xl shadow-[0_14px_36px_rgba(15,23,42,0.12)] dark:shadow-[0_18px_40px_rgba(0,0,0,0.55)] backdrop-blur-[18px] overflow-hidden p-4">
                      <p className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-300">Contribution Margin</p>
                      <p className="mt-2 text-lg font-semibold text-slate-900 dark:text-white">
                        Est: {formatCurrencyValue(metricsBreakEven?.estimated_contribution_margin ?? null, metricsProgram.currency)}
                      </p>
                      <p className="text-sm text-slate-600 dark:text-slate-300">
                        Act: {formatCurrencyValue(metricsBreakEven?.contribution_margin ?? null, metricsProgram.currency)}
                      </p>
                    </div>
                    <div className="bg-white/85 dark:bg-slate-900/70 border border-slate-100/90 dark:border-slate-700/80 rounded-3xl shadow-[0_14px_36px_rgba(15,23,42,0.12)] dark:shadow-[0_18px_40px_rgba(0,0,0,0.55)] backdrop-blur-[18px] overflow-hidden p-4">
                      <p className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-300">Break-even Students</p>
                      <p className="mt-2 text-lg font-semibold text-slate-900 dark:text-white">
                        Est: {metricsBreakEven?.estimated_break_even_exact?.toFixed(2) ?? '—'} / {metricsBreakEven?.estimated_break_even_rounded ?? '—'}
                      </p>
                      <p className="text-sm text-slate-600 dark:text-slate-300">
                        Act: {metricsBreakEven?.break_even_exact?.toFixed(2) ?? '—'} / {metricsBreakEven?.break_even_rounded ?? '—'}
                      </p>
                    </div>
                    <div className="bg-white/85 dark:bg-slate-900/70 border border-slate-100/90 dark:border-slate-700/80 rounded-3xl shadow-[0_14px_36px_rgba(15,23,42,0.12)] dark:shadow-[0_18px_40px_rgba(0,0,0,0.55)] backdrop-blur-[18px] overflow-hidden p-4">
                      <p className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-300">Break-even Revenue</p>
                      <p className="mt-2 text-lg font-semibold text-slate-900 dark:text-white">
                        Est: {formatCurrencyValue(metricsBreakEven?.estimated_break_even_revenue_exact ?? null, metricsProgram.currency)}
                      </p>
                      <p className="text-sm text-slate-600 dark:text-slate-300">
                        Act: {formatCurrencyValue(metricsBreakEven?.break_even_revenue_exact ?? null, metricsProgram.currency)}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-6">
                    <div className="bg-white/85 dark:bg-slate-900/70 border border-slate-100/90 dark:border-slate-700/80 rounded-3xl shadow-[0_14px_36px_rgba(15,23,42,0.12)] dark:shadow-[0_18px_40px_rgba(0,0,0,0.55)] backdrop-blur-[18px] overflow-hidden p-5 flex flex-col">
                      <div className="mb-3">
                        <h4 className="text-sm font-semibold text-slate-900 dark:text-white">Component Costs</h4>
                        <p className="text-xs text-slate-500 dark:text-slate-300">Estimated vs Actual totals (variable costs use seats)</p>
                      </div>
                      <div className={`grid gap-6 ${componentActualSlices.length > 0 ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1'}`}>
                        <div className="flex flex-col">
                          <p className="text-xs text-slate-500 dark:text-slate-300 mb-2">Estimated</p>
                          {componentEstimatedSlices.length === 0 ? (
                            <div className="text-sm text-slate-500 dark:text-slate-300">No estimated costs yet.</div>
                          ) : (
                            <div className="h-56 rounded-xl bg-slate-50/60 dark:bg-slate-900/50 p-2 overflow-hidden">
                              <ResponsiveContainer width="100%" height="100%">
                                <PieChart margin={{ top: 12, right: 8, bottom: 8, left: 8 }}>
                                  <Pie data={componentEstimatedSlices} dataKey="value" nameKey="name" outerRadius={80} label={false} labelLine={false}>
                                    {componentEstimatedSlices.map((entry, index) => (
                                    <Cell key={`est-${entry.name}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                                  ))}
                                </Pie>
                                <Tooltip formatter={(value: number) => formatCurrencyValue(value, metricsProgram?.currency)} />
                                <Legend wrapperStyle={{ fontSize: 10 }} />
                              </PieChart>
                            </ResponsiveContainer>
                            </div>
                          )}
                        </div>
                        {componentActualSlices.length > 0 && (
                          <div className="flex flex-col">
                            <p className="text-xs text-slate-500 dark:text-slate-300 mb-2">Actual</p>
                            <div className="h-56 rounded-xl bg-slate-50/60 dark:bg-slate-900/50 p-2 overflow-hidden">
                              <ResponsiveContainer width="100%" height="100%">
                                <PieChart margin={{ top: 12, right: 8, bottom: 8, left: 8 }}>
                                  <Pie data={componentActualSlices} dataKey="value" nameKey="name" outerRadius={80} label={false} labelLine={false}>
                                    {componentActualSlices.map((entry, index) => (
                                      <Cell key={`act-${entry.name}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                                    ))}
                                  </Pie>
                                <Tooltip formatter={(value: number) => formatCurrencyValue(value, metricsProgram?.currency)} />
                                  <Legend wrapperStyle={{ fontSize: 10 }} />
                                </PieChart>
                              </ResponsiveContainer>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="bg-white/85 dark:bg-slate-900/70 border border-slate-100/90 dark:border-slate-700/80 rounded-3xl shadow-[0_14px_36px_rgba(15,23,42,0.12)] dark:shadow-[0_18px_40px_rgba(0,0,0,0.55)] backdrop-blur-[18px] overflow-hidden p-4">
                      <div className="mb-3">
                        <h4 className="text-sm font-semibold text-slate-900 dark:text-white">Profit Table</h4>
                        <p className="text-xs text-slate-500 dark:text-slate-300">Actuals by number of students</p>
                      </div>
                      {profitTableRows.length === 0 ? (
                        <div className="text-sm text-slate-500 dark:text-slate-300">Add selling price and costs to populate.</div>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="min-w-full text-sm">
                            <thead className="border-b border-slate-200/70 dark:border-slate-700/70 text-left text-xs uppercase tracking-wide text-slate-500 dark:text-slate-300">
                              <tr>
                                <th className="py-2 pr-3">#</th>
                                <th className="py-2 pr-3">Selling Price</th>
                                <th className="py-2 pr-3">Revenue</th>
                                <th className="py-2 pr-3">VC/pp</th>
                                <th className="py-2 pr-3">VC/Total</th>
                                <th className="py-2 pr-3">TFC</th>
                                <th className="py-2 pr-3">CM (u)</th>
                                <th className="py-2">Profit</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-200/70 dark:divide-slate-700/70">
                              {profitTableRows.map((row) => (
                                <tr key={row.count} className="text-slate-700 dark:text-slate-200">
                                  <td className="py-2 pr-3 font-medium text-slate-900 dark:text-white">{row.count}</td>
                                  <td className="py-2 pr-3">{formatCurrencyValue(row.sellingPrice, metricsProgram.currency)}</td>
                                  <td className="py-2 pr-3">{formatCurrencyValue(row.revenue, metricsProgram.currency)}</td>
                                  <td className="py-2 pr-3">{formatCurrencyValue(row.variableCost, metricsProgram.currency)}</td>
                                  <td className="py-2 pr-3">{formatCurrencyValue(row.vcTotal, metricsProgram.currency)}</td>
                                  <td className="py-2 pr-3">{formatCurrencyValue(row.fixedCost, metricsProgram.currency)}</td>
                                  <td className="py-2 pr-3">{formatCurrencyValue(row.cmUnit, metricsProgram.currency)}</td>
                                  <td className="py-2">{formatCurrencyValue(row.profit, metricsProgram.currency)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 items-stretch">
                      <div className="bg-white/85 dark:bg-slate-900/70 border border-slate-100/90 dark:border-slate-700/80 rounded-3xl shadow-[0_14px_36px_rgba(15,23,42,0.12)] dark:shadow-[0_18px_40px_rgba(0,0,0,0.55)] backdrop-blur-[18px] overflow-hidden p-5 flex flex-col">
                        <div className="mb-3">
                          <h4 className="text-sm font-semibold text-slate-900 dark:text-white">Break-even Students</h4>
                          <p className="text-xs text-slate-500 dark:text-slate-300">Current break-even by track</p>
                        </div>
                        <div className="h-64 rounded-xl bg-slate-50/60 dark:bg-slate-900/50 p-2 overflow-hidden">
                          {pathwayPieData.length === 0 ? (
                            <div className="text-sm text-slate-500 dark:text-slate-300">No break-even data yet.</div>
                          ) : (
                            <ResponsiveContainer width="100%" height="100%">
                              <PieChart margin={{ top: 12, right: 8, bottom: 8, left: 8 }}>
                                <Pie data={pathwayPieData} dataKey="value" nameKey="name" outerRadius={90} label={false} labelLine={false}>
                                  {pathwayPieData.map((entry, index) => (
                                    <Cell key={`pkg-${entry.name}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                                  ))}
                                </Pie>
                                <Tooltip formatter={(value: number) => Math.round(value).toLocaleString()} />
                                <Legend wrapperStyle={{ fontSize: 10 }} />
                              </PieChart>
                            </ResponsiveContainer>
                          )}
                        </div>
                      </div>
                      <div className="bg-white/85 dark:bg-slate-900/70 border border-slate-100/90 dark:border-slate-700/80 rounded-3xl shadow-[0_14px_36px_rgba(15,23,42,0.12)] dark:shadow-[0_18px_40px_rgba(0,0,0,0.55)] backdrop-blur-[18px] overflow-hidden p-5 flex flex-col">
                        <div className="mb-3">
                          <h4 className="text-sm font-semibold text-slate-900 dark:text-white">Track Price Comparison</h4>
                          <p className="text-xs text-slate-500 dark:text-slate-300">Estimated vs actual selling price</p>
                        </div>
                        {pathwayPriceData.length === 0 ? (
                          <div className="text-sm text-slate-500 dark:text-slate-300">No pathway pricing yet.</div>
                        ) : (
                          <div className="h-64 rounded-xl bg-slate-50/60 dark:bg-slate-900/50 p-2 overflow-hidden">
                            <ResponsiveContainer width="100%" height="100%">
                              <BarChart data={pathwayPriceData} margin={{ top: 8, right: 12, bottom: 16, left: 4 }}>
                                <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-15} textAnchor="end" height={50} />
                                <YAxis tick={{ fontSize: 10 }} />
                                <Tooltip formatter={(value: number) => formatCurrencyValue(value, metricsProgram?.currency)} />
                                <Legend wrapperStyle={{ fontSize: 10 }} />
                                <Bar dataKey="estimated" fill="#60a5fa" name="Estimated" />
                                <Bar dataKey="actual" fill="#34d399" name="Actual" />
                              </BarChart>
                            </ResponsiveContainer>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </>
              )}
              </div>
            </div>
          </div>
        </div>
      )}

      {showBuilderModal && builderProgramId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-slate-900/60" onClick={closeBuilderModal} />
          <div className="bg-white/85 dark:bg-slate-900/70 border border-slate-100/90 dark:border-slate-700/80 rounded-3xl shadow-[0_14px_36px_rgba(15,23,42,0.12)] dark:shadow-[0_18px_40px_rgba(0,0,0,0.55)] backdrop-blur-[18px] overflow-hidden overflow-y-auto overflow-x-hidden relative z-50 w-full max-w-6xl mx-4 max-h-[92vh]">
            <div className="flex items-start justify-between px-6 py-4 border-b border-slate-200/70 dark:border-slate-700/70">
              <div>
                <p className="text-sm uppercase tracking-wide text-slate-500 dark:text-slate-300">Program Builder</p>
                <h3 className="text-2xl font-semibold text-slate-900 dark:text-white">Study Tracks & Extensions</h3>
              </div>
              <button onClick={closeBuilderModal} className="text-slate-500 hover:text-slate-700 dark:text-slate-300 dark:hover:text-slate-100">
                ✕
              </button>
            </div>
            <div className="px-6 py-6">
              <PathwaySelectionBuilder
                embedded
                hideProgramSelect
                initialProgramId={builderProgramId}
                initialProgram={builderProgram}
              />
            </div>
          </div>
        </div>
      )}

      <style>
        {`
          @media print {
            body * {
              visibility: hidden !important;
            }
            .print-target,
            .print-target * {
              visibility: visible !important;
            }
            .print-target {
              position: static !important;
              margin: 0 !important;
              max-height: none !important;
              box-shadow: none !important;
            }
            .print-target .overflow-y-auto {
              overflow: visible !important;
            }
            .print-hide {
              display: none !important;
            }
          }
        `}
      </style>
    </div>
  );
};

export default ProgramsManagement;
